import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { defaultValue, } from '../model/department.model';
import { appendCriteriaContains, appendCriteriaEquals, appendPageable } from '../utils/spring-api';
import { resolveAsyncThunkErrorMessage, serializeAxiosError } from './reducer.utils';
const initialState = {
    loading: false,
    errorMessage: null,
    departments: [],
    department: defaultValue,
    updating: false,
    updateSuccess: false,
    totalItems: 0,
    totalAllItems: 0,
};
const apiUrl = API_ROUTES.crmDepartments;
function buildDepartmentQuery({ page, size, sort, name, active }) {
    const q = new URLSearchParams();
    appendPageable(q, page, size, sort);
    appendCriteriaContains(q, 'name', name);
    if (active !== undefined) {
        appendCriteriaEquals(q, 'active', active);
    }
    return q.toString();
}
// Async Actions
export const getDepartments = createAsyncThunk('departmentManagement/fetch_departments', async (params) => {
    const requestUrl = `${apiUrl}?${buildDepartmentQuery(params)}`;
    return axios.get(requestUrl);
}, { serializeError: serializeAxiosError });
export const getDepartmentCount = createAsyncThunk('departmentManagement/fetch_department_count', async () => {
    const requestUrl = `${apiUrl}/count`;
    return axios.get(requestUrl);
}, { serializeError: serializeAxiosError });
export const getDepartment = createAsyncThunk('departmentManagement/fetch_department', async (id) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get(requestUrl);
}, { serializeError: serializeAxiosError });
export const createDepartment = createAsyncThunk('departmentManagement/create_department', async (entity) => {
    const result = await axios.post(apiUrl, entity);
    return result;
}, { serializeError: serializeAxiosError });
export const updateDepartment = createAsyncThunk('departmentManagement/update_department', async (entity) => {
    const result = await axios.put(`${apiUrl}/${entity.id}`, entity);
    return result;
}, { serializeError: serializeAxiosError });
export const deleteDepartment = createAsyncThunk('departmentManagement/delete_department', async (id) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete(requestUrl);
    return result;
}, { serializeError: serializeAxiosError });
export const DepartmentManagementSlice = createSlice({
    name: 'departmentManagement',
    initialState: initialState,
    reducers: {
        reset() {
            return initialState;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getDepartment.fulfilled, (state, action) => {
            state.loading = false;
            state.department = action.payload.data;
        })
            .addCase(getDepartmentCount.fulfilled, (state, action) => {
            state.totalAllItems = Number(action.payload.data) || 0;
        })
            .addCase(deleteDepartment.fulfilled, (state) => {
            state.updating = false;
            state.updateSuccess = true;
            state.department = defaultValue;
        })
            .addMatcher(isFulfilled(getDepartments), (state, action) => {
            state.loading = false;
            state.departments = action.payload.data;
            state.totalItems = parseInt(action.payload.headers['x-total-count'], 10);
        })
            .addMatcher(isFulfilled(createDepartment, updateDepartment), (state, action) => {
            state.updating = false;
            state.loading = false;
            state.updateSuccess = true;
            state.department = action.payload.data;
        })
            .addMatcher(isPending(getDepartments, getDepartment), (state) => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.loading = true;
        })
            .addMatcher(isPending(createDepartment, updateDepartment, deleteDepartment), (state) => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.updating = true;
        })
            .addMatcher(isRejected(getDepartments, getDepartment, createDepartment, updateDepartment, deleteDepartment), (state, action) => {
            state.loading = false;
            state.updating = false;
            state.updateSuccess = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { reset } = DepartmentManagementSlice.actions;
// Reducer
export default DepartmentManagementSlice.reducer;
